import { useRef, useState, useEffect } from 'react';
import { getTablesTheme } from 'components/CustomThemes';
import { lighten, ThemeProvider } from '@mui/material/styles';
import {
  MRT_ToggleGlobalFilterButton,
  MRT_ShowHideColumnsButton,
  MaterialReactTable,
  useMaterialReactTable,
  MRT_GlobalFilterTextField,
} from 'material-react-table';
import Grid from '@mui/material/Grid2';
import { Stack, Box, Typography, Button } from '@mui/material';
import intl from 'react-intl-universal';
import { TABLE_LOCALE } from 'shared/constants/tableOptions';
import CustomRangePicker from 'components/CustomRangePicker';
import { RefreshButton } from 'components';
import PropTypes from 'prop-types';
import { USER_DATA } from 'shared/constants/users';
import { TABLE_TYPE } from 'components/PayrollTable/constants';
import { useMaterialUIController } from 'context';
import { useLocation } from 'react-router-dom';

const propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  tableTitle: PropTypes.string.isRequired,
  timeRangeStart: PropTypes.instanceOf(Date).isRequired,
  timeRangeEnd: PropTypes.instanceOf(Date).isRequired,
  updateTimeRangeStart: PropTypes.func.isRequired,
  updateTimeRangeEnd: PropTypes.func.isRequired,
  handleRefreshClick: PropTypes.func.isRequired,
  setTableType: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  tableType: PropTypes.string.isRequired,
  multiSelectComponent: PropTypes.func,
  enableRowSelection: PropTypes.bool,
  columnOrder: PropTypes.array, // Used to set the initial column order
};

const PayrollTable = ({
  columns,
  data,
  tableTitle,
  timeRangeStart,
  timeRangeEnd,
  updateTimeRangeStart,
  updateTimeRangeEnd,
  handleRefreshClick,
  subtitle,
  setTableType,
  tableType,
  multiSelectComponent,
  enableRowSelection,
  columnOrder,
}) => {
  const locale = localStorage.getItem(USER_DATA.PREFFERED_LANGUAGE);
  const { darkMode } = useMaterialUIController();
  const [isStartMonthPickerOpen, setIsStartMonthPickerOpen] = useState(false);
  const [isEndMonthPickerOpen, setIsEndMonthPickerOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);

  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);

  // Closing the picker when the location changes
  useEffect(() => {
    if (pathname !== pathnameRef.current) {
      setIsStartMonthPickerOpen(false);
      setIsEndMonthPickerOpen(false);
    }
  }, [pathname]);

  const table = useMaterialReactTable({
    columns,
    data,
    localization: TABLE_LOCALE[locale],
    muiTableContainerProps: {
      sx: { maxHeight: '950px' },
    },
    // TODO Dark Mode
    muiTableHeadProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTablePaperProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: '#ffffff', // Set cells background to white
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: '#ffffff', // Light gray for header contrast
      },
    },
    enableColumnFilterModes: false,
    enableGrouping: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableRowSelection: enableRowSelection,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
      density: 'compact',
      columnOrder,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 20, 30],
    },
    onColumnFiltersChange: (updatedColumnFilters) => {
      setColumnFilters(updatedColumnFilters);
      setIsStartMonthPickerOpen(false);
      setIsEndMonthPickerOpen(false);
    },
    onSortingChange: (updatedSorting) => {
      setSorting(updatedSorting);
      setIsStartMonthPickerOpen(false);
      setIsEndMonthPickerOpen(false);
    },
    onRowSelectionChange: (updater) => {
      setIsStartMonthPickerOpen(false);
      setIsEndMonthPickerOpen(false);
      setRowSelection(updater);
    },
    getFacetedUniqueValues: (table, columnId) => () => {
      const uniqueValueMap = new Map();
      table.getPreFilteredRowModel().rows.forEach((row) => {
        const value = row.original[columnId];
        if (Array.isArray(value)) {
          value.forEach((tag) => {
            uniqueValueMap.set(tag, (uniqueValueMap.get(tag) || 0) + 1);
          });
        } else if (value !== undefined && value !== null) {
          // Handle non-array values
          uniqueValueMap.set(value, (uniqueValueMap.get(value) || 0) + 1);
        }
      });
      return uniqueValueMap;
    },
    renderTopToolbar: ({ table }) => {
      const selectedRows = table.getFilteredSelectedRowModel().rows;

      return (
        <Stack sx={{ m: '2rem 0' }}>
          <Box sx={{ pl: '10px' }}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {tableTitle}
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              backgroundColor: lighten(theme.palette.background.default, 0.05),
              display: 'flex',
              gap: '0.5rem',
              p: '8px',
              justifyContent: 'space-between',
            })}>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <CustomRangePicker
                isStartDatePickerOpen={isStartMonthPickerOpen}
                setIsStartDatePickerOpen={setIsStartMonthPickerOpen}
                isEndDatePickerOpen={isEndMonthPickerOpen}
                setIsEndDatePickerOpen={setIsEndMonthPickerOpen}
                timeRangeStart={timeRangeStart}
                timeRangeEnd={timeRangeEnd}
                updateTimeRangeStart={updateTimeRangeStart}
                updateTimeRangeEnd={updateTimeRangeEnd}
                darkMode={darkMode}
                isMonthOnly={true}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {}
              <MRT_GlobalFilterTextField table={table} />
              {}
              <MRT_ToggleGlobalFilterButton table={table} />
              {}
              <MRT_ShowHideColumnsButton table={table} />
              <RefreshButton
                darkMode={darkMode}
                tooltipTitle={intl.get(
                  'dashboard_refresh_button_tooltip_title'
                )}
                handleRefreshClick={() => {
                  handleRefreshClick();
                  table.resetRowSelection();
                }}
              />
            </Box>
          </Box>
          {tableType !== TABLE_TYPE.SUMMARY && (
            <Grid container justifyContent="space-between">
              <Grid>
                <Box
                  sx={{
                    pl: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                  <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                    {subtitle}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setTableType(TABLE_TYPE.SUMMARY);
                      table.resetRowSelection();
                    }}>
                    {intl.get(
                      'billing_for_sent_cases_button_go_to_summary_table'
                    )}
                  </Button>
                </Box>
              </Grid>

              {selectedRows.length > 0 && (
                <Grid>{multiSelectComponent(table)}</Grid>
              )}
            </Grid>
          )}
        </Stack>
      );
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: {
        cursor: 'pointer',
      },
    }),
    state: { isLoading: !data, rowSelection, columnFilters, sorting },
  });

  return (
    <Box>
      <ThemeProvider theme={getTablesTheme(darkMode)}>
        <MaterialReactTable table={table} />
      </ThemeProvider>
    </Box>
  );
};

PayrollTable.propTypes = propTypes;

export default PayrollTable;
